<template>
    <section class="mobile-question-page">
        <header class="mobile-question-header">
            <div class="mobile-question-header__brand">
                <img class="mobile-question-header__logo" src="/static/bbs/images/login/logo_black.png" alt="BIGEMAP">
                <img class="mobile-question-header__title" src="/static/bbs/images/index/logo_text.png" alt="知识库">
            </div>
            <button class="mobile-question-header__user" type="button" aria-label="进入我的" @click="openUser">
                <img v-if="avatarUrl" :src="avatarUrl" alt="用户头像" @error="avatarFailed = true">
                <svg v-else viewBox="0 0 24 24" aria-hidden="true">
                    <circle cx="12" cy="8" r="3.25" />
                    <path d="M5.25 19.25c.55-3.35 3.05-5.25 6.75-5.25s6.2 1.9 6.75 5.25" />
                </svg>
            </button>
        </header>

        <div class="mobile-question-hero">
            <button class="mobile-question-search" type="button" @click="searchVisible = true">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <circle cx="10.75" cy="10.75" r="6.25" />
                    <path d="m15.5 15.5 4 4" />
                </svg>
                <span>搜索产品问题、教程、报错关键词</span>
            </button>
            <div class="mobile-question-actions">
                <p>你的问题将在 10 分钟内得到官方回答</p>
                <button type="button" @click="addQuestion">
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M12 5v14M5 12h14" />
                    </svg>
                    我要提问
                </button>
            </div>
        </div>

        <div class="mobile-question-tabs" role="tablist" aria-label="问题分类">
            <button
                v-for="item in categories"
                :key="item.name"
                type="button"
                role="tab"
                :aria-selected="activeCategory === item.name"
                :class="{'mobile-question-tabs__item--active': activeCategory === item.name}"
                class="mobile-question-tabs__item"
                @click="selectCategory(item)"
            >
                {{ item.title }}
            </button>
        </div>

        <main class="mobile-question-list">
            <div class="mobile-question-list__title">
                <div>
                    <h1>{{ activeCategoryTitle }}</h1>
                    <p>产品使用问题、教程与经验分享</p>
                </div>
                <span v-if="questions.length">{{ questions.length }} 条</span>
            </div>

            <template v-if="loading && questions.length === 0">
                <div v-for="item in 3" :key="item" class="mobile-question-skeleton">
                    <span></span>
                    <strong></strong>
                    <i></i>
                    <i></i>
                </div>
            </template>

            <div v-else-if="error && questions.length === 0" class="mobile-question-state">
                <div class="mobile-question-state__icon">!</div>
                <p>内容暂时没有加载出来</p>
                <button type="button" @click="loadQuestions(true)">重新加载</button>
            </div>

            <div v-else-if="questions.length === 0" class="mobile-question-state">
                <div class="mobile-question-state__icon">问</div>
                <p>这个分类暂时还没有内容</p>
            </div>

            <template v-else>
                <mobile-question-card
                    v-for="question in questions"
                    :key="question.answer_id"
                    :question="question"
                ></mobile-question-card>
                <button
                    v-if="hasMore"
                    class="mobile-question-more"
                    type="button"
                    :disabled="loading"
                    @click="loadMore"
                >
                    {{ loading ? '正在加载…' : '加载更多' }}
                </button>
                <p v-else class="mobile-question-end">没有更多内容了</p>
            </template>
        </main>

        <mobile-question-search
            v-if="searchVisible"
            @close="searchVisible = false"
        ></mobile-question-search>
    </section>
</template>

<script>
import MobileQuestionCard from '@/components/mobile/wenda/question-card.vue';
import MobileQuestionSearch from '@/components/mobile/wenda/search.vue';
import storage from '@/utils/storage';

export default {
    name: 'MobileQuestion',
    components: {
        MobileQuestionCard,
        MobileQuestionSearch,
    },
    data() {
        return {
            searchVisible: false,
            avatarFailed: false,
            activeCategory: 'recommend',
            questions: [],
            pageNumber: 1,
            hasMore: false,
            loading: false,
            error: false,
            categories: [
                {title: '推荐', name: 'recommend'},
                {title: '资讯', name: 'news'},
                {title: '热榜', name: 'hot'},
                {title: 'BIGEMAP PRO', name: 'pro'},
                {title: 'SERVER', name: 'server'},
                {title: 'APP', name: 'app'},
            ],
            urls: {
                recommend: '/bbs/hot/recommend',
                news: '/bbs/news/index',
                hot: '/bbs/hot/index',
                default: '/bbs/answer/index',
            },
        };
    },
    computed: {
        userData() {
            return this.$store.state.userData || {};
        },
        avatarUrl() {
            if (!storage.token() || this.avatarFailed) {
                return '';
            }
            return this.userData.face || '';
        },
        activeCategoryTitle() {
            const current = this.categories.find((item) => item.name === this.activeCategory);
            return current ? current.title : '推荐';
        },
    },
    watch: {
        '$store.state.userData.face': function () {
            this.avatarFailed = false;
        },
        '$route.params.type': function () {
            this.syncCategoryFromRoute();
        },
    },
    created() {
        this.syncCategoryFromRoute();
    },
    methods: {
        syncCategoryFromRoute() {
            const routeType = this.$route.params && this.$route.params.type;
            const exists = this.categories.some((item) => item.name === routeType);
            const nextCategory = exists ? routeType : 'recommend';
            if (this.activeCategory === nextCategory && this.questions.length) {
                return;
            }
            this.activeCategory = nextCategory;
            this.loadQuestions(true);
        },
        selectCategory(item) {
            if (item.name === this.activeCategory) {
                return;
            }
            this.$router.push({path: `/wenda/${item.name}`});
        },
        loadQuestions(reset) {
            if (this.loading) {
                return;
            }
            if (reset) {
                this.pageNumber = 1;
                this.questions = [];
            }
            this.loading = true;
            this.error = false;
            const url = this.urls[this.activeCategory] || this.urls.default;
            this.$bbs.get(url, {
                params: {
                    pageSize: 10,
                    pageNumber: this.pageNumber,
                    type: 'hot',
                    search: {
                        pageNumber: this.pageNumber,
                        answer_id: [],
                        type_id: this.activeCategory,
                    },
                },
                need: {
                    loading: false,
                },
            }).then((response) => {
                const payload = response.data || {};
                const list = Array.isArray(payload.hot)
                    ? payload.hot
                    : Array.isArray(payload.data) ? payload.data : [];
                const existingIds = new Set(this.questions.map((item) => String(item.answer_id)));
                const uniqueList = list.filter((item) => {
                    const answerId = item && item.answer_id;
                    if (typeof answerId === 'undefined' || answerId === null) {
                        return false;
                    }
                    const key = String(answerId);
                    if (existingIds.has(key)) {
                        return false;
                    }
                    existingIds.add(key);
                    return true;
                });
                this.questions.push(...uniqueList);
                this.hasMore = list.length === 10 && uniqueList.length > 0;
            }).catch(() => {
                if (!reset && this.pageNumber > 1) {
                    this.pageNumber -= 1;
                }
                this.error = true;
            }).finally(() => {
                this.loading = false;
            });
        },
        loadMore() {
            this.pageNumber += 1;
            this.loadQuestions(false);
        },
        addQuestion() {
            if (!storage.token()) {
                this.$router.push({path: '/login', query: {redirect: '/add'}});
                return;
            }
            this.$router.push({path: '/add'});
        },
        openUser() {
            if (!storage.token()) {
                this.$router.push({path: '/login', query: {redirect: '/user/profile/setting'}});
                return;
            }
            this.$router.push({path: '/user/profile/setting'});
        },
    },
};
</script>

<style scoped lang="less">
.mobile-question-page {
    min-height: 100vh;
    color: #172033;
    background: #f4f6f9;
}

.mobile-question-header {
    display: flex;
    height: 54px;
    padding: 0 16px;
    align-items: center;
    justify-content: space-between;
    background: #fff;
    border-bottom: 1px solid #edf0f4;
}

.mobile-question-header__brand {
    display: flex;
    align-items: center;
    gap: 8px;

    .mobile-question-header__logo {
        display: block;
        width: 112px;
        height: auto;
    }

    .mobile-question-header__title {
        display: block;
        width: 52px;
        height: auto;
        transform: translateY(2px);
    }
}

.mobile-question-header__user {
    display: flex;
    width: 36px;
    height: 36px;
    padding: 0;
    align-items: center;
    justify-content: center;
    color: #5e6a7f;
    background: #f5f7fa;
    border: 0;
    border-radius: 50%;
    overflow: hidden;

    img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: inherit;
    }

    svg {
        width: 21px;
        height: 21px;
        fill: none;
        stroke: currentColor;
        stroke-width: 1.8;
        stroke-linecap: round;
    }
}

.mobile-question-hero {
    padding: 18px 16px 16px;
    background: linear-gradient(145deg, #edf5ff, #f8fbff);
    border-bottom: 1px solid #e5edf8;
}

.mobile-question-search {
    display: flex;
    width: 100%;
    height: 46px;
    padding: 0 14px;
    align-items: center;
    gap: 10px;
    color: #8a94a6;
    font-size: 14px;
    text-align: left;
    background: #fff;
    border: 1px solid #dce7f5;
    border-radius: 8px;
    box-shadow: 0 5px 16px rgba(52, 96, 158, 0.07);

    svg {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
        fill: none;
        stroke: #2478f2;
        stroke-width: 1.8;
        stroke-linecap: round;
    }
}

.mobile-question-actions {
    display: flex;
    margin-top: 14px;
    align-items: center;
    justify-content: space-between;
    gap: 12px;

    p {
        margin: 0;
        color: #6f7b8f;
        font-size: 12px;
        line-height: 18px;
    }

    button {
        display: flex;
        height: 36px;
        padding: 0 14px;
        flex-shrink: 0;
        align-items: center;
        gap: 5px;
        color: #fff;
        font-size: 13px;
        font-weight: 500;
        background: #2478f2;
        border: 0;
        border-radius: 6px;

        svg {
            width: 16px;
            height: 16px;
            fill: none;
            stroke: currentColor;
            stroke-width: 2;
            stroke-linecap: round;
        }
    }
}

.mobile-question-tabs {
    display: flex;
    padding: 12px 16px;
    gap: 8px;
    overflow-x: auto;
    background: #fff;
    border-bottom: 1px solid #edf0f4;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
}

.mobile-question-tabs__item {
    height: 32px;
    padding: 0 13px;
    flex-shrink: 0;
    color: #657187;
    font-size: 13px;
    white-space: nowrap;
    background: #f4f6f9;
    border: 1px solid transparent;
    border-radius: 16px;
}

.mobile-question-tabs__item--active {
    color: #2478f2;
    font-weight: 500;
    background: #edf4ff;
    border-color: #bfd5fb;
}

.mobile-question-list {
    padding: 16px 12px 24px;
}

.mobile-question-list__title {
    display: flex;
    padding: 0 4px 12px;
    align-items: flex-end;
    justify-content: space-between;

    h1 {
        margin: 0;
        color: #172033;
        font-size: 18px;
        font-weight: 600;
    }

    p {
        margin: 4px 0 0;
        color: #98a2b3;
        font-size: 12px;
    }

    > span {
        color: #98a2b3;
        font-size: 12px;
    }
}

.mobile-question-skeleton {
    padding: 16px;
    margin-bottom: 10px;
    background: #fff;
    border: 1px solid #edf0f4;
    border-radius: 8px;

    span,
    strong,
    i {
        display: block;
        height: 11px;
        margin-bottom: 11px;
        background: #edf0f4;
        border-radius: 5px;
        animation: mobile-skeleton 1.2s ease-in-out infinite alternate;
    }

    span {
        width: 34%;
    }

    strong {
        width: 88%;
        height: 16px;
    }

    i:nth-last-child(2) {
        width: 100%;
    }

    i:last-child {
        width: 66%;
        margin-bottom: 0;
    }
}

.mobile-question-state {
    display: flex;
    min-height: 260px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #8b96a8;
    background: #fff;
    border: 1px solid #edf0f4;
    border-radius: 8px;

    p {
        margin: 12px 0;
        font-size: 14px;
    }

    button {
        padding: 7px 16px;
        color: #2478f2;
        background: #edf4ff;
        border: 0;
        border-radius: 5px;
    }
}

.mobile-question-state__icon {
    display: flex;
    width: 44px;
    height: 44px;
    align-items: center;
    justify-content: center;
    color: #2478f2;
    font-size: 20px;
    font-weight: 600;
    background: #edf4ff;
    border-radius: 14px;
}

.mobile-question-more {
    width: 100%;
    height: 42px;
    margin-top: 4px;
    color: #5d6a7e;
    font-size: 13px;
    background: #fff;
    border: 1px solid #e4e9f0;
    border-radius: 7px;
}

.mobile-question-end {
    margin: 16px 0 0;
    color: #a0a9b8;
    font-size: 12px;
    text-align: center;
}

@keyframes mobile-skeleton {
    from {
        opacity: 0.55;
    }
    to {
        opacity: 1;
    }
}
</style>
